import { JsonValue } from "@bufbuild/protobuf";

import React, { useCallback, useEffect, useRef } from "react";

import { Box } from "@mui/material";

import type { Template } from "@safetyculture/s12-apis-connect-web/s12/templates/v1/template_pb";

import useStyles from "components/search/styles";
import { SC_APP_URL } from "constants/index";

export const TOPICS = {
  ERROR: "ul.frameError",
  FRAME_READY: "ul.frameReady",
  TEMPLATE_DATA: "cl.templateData",
} as const;

interface Message {
  topic: (typeof TOPICS)[keyof typeof TOPICS];
  payload?: unknown;
}

interface MessageFrameReady extends Message {
  topic: typeof TOPICS.FRAME_READY;
}

interface MessageError extends Message {
  topic: typeof TOPICS.ERROR;
}

interface MessageTemplateData extends Message {
  topic: typeof TOPICS.TEMPLATE_DATA;
  payload: JsonValue;
}

export type IncomingMessage = MessageFrameReady | MessageError;
export type OutgoingMessage = MessageTemplateData;

interface InspectionIframeProps {
  onError: () => void;
  onReady: () => void;
  template: Template;
}

export function InspectionIframe({
  onError,
  onReady,
  template,
}: InspectionIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const messagePort = useRef<MessagePort | null>(null);
  const clSearchClasses = useStyles();

  const postMessage = useCallback((message: OutgoingMessage) => {
    if (messagePort.current == null) {
      throw new Error("Port not ready");
    }

    messagePort.current.postMessage(message);
  }, []);

  const handleMessage = useCallback(
    ({ topic }: IncomingMessage) => {
      switch (topic) {
        case TOPICS.ERROR:
          onError();
          break;
      }
    },
    [onError],
  );

  // set up the message listener
  useEffect(() => {
    function initPort(newPort: MessagePort) {
      if (messagePort.current != null) {
        throw new Error("Port already initialized");
      }
      messagePort.current = newPort;
      messagePort.current.onmessage = (
        event: MessageEvent<IncomingMessage>,
      ) => {
        handleMessage(event.data);
      };
      postMessage({
        topic: TOPICS.TEMPLATE_DATA,
        payload: template.toJson(),
      });
    }

    const handleWindowMessage = (event: MessageEvent<IncomingMessage>) => {
      // If port exists, the port will handle the message
      if (event.origin !== SC_APP_URL || messagePort.current != null) {
        return;
      }

      switch (event.data.topic) {
        case TOPICS.ERROR:
          onError();
          break;
        case TOPICS.FRAME_READY:
          if (event.ports[0] != null) {
            initPort(event.ports[0]);
          }
          onReady();
          break;
      }
    };

    window.addEventListener("message", handleWindowMessage);

    return () => {
      window.removeEventListener("message", handleWindowMessage);
    };
  }, [handleMessage, onError, onReady, postMessage, template]);

  return (
    <Box
      component="iframe"
      ref={iframeRef}
      src={`${SC_APP_URL}/template-preview`}
      title={"Inspection template preview"}
      sx={clSearchClasses.iframeContainer}
      style={{ border: 0 }}
    />
  );
}
